// services/firebase.ts
import { UserProfile, TeamMember, UserRole, BlogPost, FactoryDrop, ProductionItem } from '../types';
import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { getAnalytics, logEvent, Analytics, isSupported } from "firebase/analytics";
import { 
  getAuth, 
  signInWithPopup, 
  signInWithRedirect, 
  GoogleAuthProvider, 
  signOut as firebaseSignOut, 
  onAuthStateChanged,
  updateProfile,
  User, 
  Auth 
} from 'firebase/auth';
import { getFirestore, collection, doc, setDoc, getDoc, getDocs, addDoc, deleteDoc, query, where, serverTimestamp, orderBy, Firestore, updateDoc, increment } from 'firebase/firestore/lite';
import { getStorage, ref, uploadBytes, getDownloadURL, FirebaseStorage} from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';

const env = (import.meta as any).env || {};

const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
  measurementId: env.VITE_FIREBASE_MEASUREMENT_ID
};

const isRealFirebase = !!firebaseConfig.apiKey;
const isBrowser = typeof window !== 'undefined'; // Helper for SSR/Prerender check

// Kept ONLY for fallback/local testing environment
const ADMIN_EMAILS = [
  'fivedollarworkday@gmail.com', 
  'jeremy@researchengineer.com'
];

let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let analytics: Analytics | null = null; 

if (isRealFirebase) {
  try {
    app = !getApps().length ? initializeApp(firebaseConfig) : getApp();
    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
    
    // ✅ SAFEGUARD: Analytics initialization only runs in the browser
    if (isBrowser) {
      isSupported().then((supported) => {
        if (supported) {
          analytics = getAnalytics(app);
        }
      });
    }
    
    console.log('Firebase initialized successfully');
  } catch (error) {
    console.error('Firebase initialization error:', error);
  }
} else {
  console.log('Running in Fallback Mode (No Firebase Config detected)');
}

const createSlug = (text: string) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')     
    .replace(/[^\w\-]+/g, '') 
    .replace(/\-\-+/g, '-')   
    .replace(/^-+/, '')       
    .replace(/-+$/, '');      
};

// --- ADMIN VERIFICATION ---
export const checkIsAdmin = async (uid: string | undefined): Promise<boolean> => {
  if (!uid || !isRealFirebase) return false;
  
  try {
    const adminDocRef = doc(db, 'admins', uid);
    const adminSnap = await getDoc(adminDocRef);
    return adminSnap.exists();
  } catch (error) {
    console.error("Failed to verify admin status:", error);
    return false;
  }
};

// --- AUTH SERVICE ---
const mapFirebaseUserToProfile = async (user: User): Promise<UserProfile> => {
  let isPro = false;
  let usageCount = 0;
  let usageLimit = 5; 
  let usageResetDate = new Date().toISOString();

  let bankedFreeMonths = 0;
  let proUntil = null;
  let subscriptionId = null;
  let stripeCustomerId = null;

  let isAdmin = false;

  if (isRealFirebase) {
    isAdmin = await checkIsAdmin(user.uid); // Dynamic Firestore lookup
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (userDoc.exists()) {
      const data = userDoc.data();
      
      const now = new Date();
      const proUntilTimestamp = data.proUntil ? data.proUntil.toDate() : new Date(0);
      const isPromotionalPro = now < proUntilTimestamp;

      // Dynamic cross-app permission mapping
      isPro = data.isPro_codetotext === true || 
              data.isPro === true || 
              data.plan === 'pro' || 
              data.subscriptionStatus === 'active' ||
              isPromotionalPro;
      
      bankedFreeMonths = data.bankedFreeMonths_codetotext || 0;
      proUntil = data.proUntil || null;
      subscriptionId = data.subscriptionId || null;
      stripeCustomerId = data.stripeCustomerId || null;

      const lastReset = data.lastUsageReset ? data.lastUsageReset.toDate() : new Date(0);
      const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
      if (now.getTime() - lastReset.getTime() > oneWeekMs) {
        usageCount = 0;
      } else {
        usageCount = data.usageCount || 0;
      }
      usageResetDate = lastReset.toISOString();
    }
  } else {
    // ✅ SAFEGUARD: localStorage check for fallback
    isAdmin = user.email ? ADMIN_EMAILS.includes(user.email) : false;
    const stored = isBrowser ? localStorage.getItem('ctt_user') : null;
    if (stored) {
      const parsed = JSON.parse(stored);
      isPro = parsed.isPro;
      usageCount = parsed.usageCount || 0;
      bankedFreeMonths = parsed.bankedFreeMonths || 0;
    }
  }
  if (isPro) usageLimit = 9999;
  return {
    uid: user.uid,
    displayName: user.displayName,
    email: user.email,
    photoURL: user.photoURL,
    isPro,
    isAdmin,
    usageCount,
    usageLimit,
    usageResetDate,
    bankedFreeMonths,
    proUntil,
    subscriptionId,
    stripeCustomerId
  };
};

export const subscribeToAuthChanges = (callback: (user: UserProfile | null) => void) => {
  if (isRealFirebase) {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const profile = await mapFirebaseUserToProfile(firebaseUser);
        const userRef = doc(db, 'users', firebaseUser.uid);
        const existingSnap = await getDoc(userRef);
        const isNewUser = !existingSnap.exists();

        await setDoc(userRef, {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            lastLogin: serverTimestamp(),
            ...(isNewUser && { createdByApp: 'fdw' }),
        }, { merge: true });
        callback(profile);
      } else {
        callback(null);
      }
    });
  } else {
    // ✅ SAFEGUARD: localStorage check
    const stored = isBrowser ? localStorage.getItem('ctt_user') : null;
    if (stored) {
      const u = JSON.parse(stored);
      if (u.email && ADMIN_EMAILS.includes(u.email)) {
        u.isAdmin = true;
      } else {
        u.isAdmin = false;
      }
      callback(u);
    } else {
      callback(null);
    }
    return () => {}; 
  }
};

export const incrementUsageCount = async (uid: string): Promise<void> => {
  if (isRealFirebase) {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    const data = userDoc.data();
    const now = new Date();
    const lastReset = data?.lastUsageReset ? data.lastUsageReset.toDate() : new Date(0);
    const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
    let newCount = (data?.usageCount || 0) + 1;
    let newResetDate = lastReset;
    if (now.getTime() - lastReset.getTime() > oneWeekMs) {
      newCount = 1;
      newResetDate = now;
    }
    await updateDoc(userRef, {
      usageCount: newCount,
      lastUsageReset: newResetDate
    });
  } else {
    // ✅ SAFEGUARD: localStorage check
    if (isBrowser) {
      const stored = localStorage.getItem('ctt_user');
      if (stored) {
        const user = JSON.parse(stored);
        user.usageCount = (user.usageCount || 0) + 1;
        localStorage.setItem('ctt_user', JSON.stringify(user));
      }
    }
  }
};

export const mockSignInWithGoogle = async (): Promise<void> => {
  if (isRealFirebase) {
    const provider = new GoogleAuthProvider();
    await signInWithRedirect(auth, provider);
  } else {
    return new Promise((resolve) => {
      setTimeout(() => {
        const mockUser: UserProfile = {
          uid: 'user_admin',
          displayName: 'Five Dollar Workday',
          email: 'fivedollarworkday@gmail.com',
          photoURL: 'https://ui-avatars.com/api/?name=Five+Dollar',
          isPro: true,
          isAdmin: true
        };
        if (isBrowser) {
          localStorage.setItem('ctt_user', JSON.stringify(mockUser));
          window.location.reload(); 
        }
        resolve();
      }, 800);
    });
  }
};

export const mockSignOut = async (): Promise<void> => {
  if (isRealFirebase) {
    await firebaseSignOut(auth);
  } else {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (isBrowser) {
          localStorage.removeItem('ctt_user');
          window.location.reload();
        }
        resolve();
      }, 500);
    });
  }
};

export const getStoredUser = (): UserProfile | null => {
  if (!isBrowser) return null;
  const stored = localStorage.getItem('ctt_user');
  if (stored) return JSON.parse(stored);
  return null;
};

export const updateUser = async (uid: string, updates: { displayName?: string; photoFile?: File }): Promise<UserProfile> => {
  if (isRealFirebase) {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user found");
    let photoURL = user.photoURL;
    if (updates.photoFile) {
      const storageRef = ref(storage, `users/${uid}/${updates.photoFile.name}`);
      await uploadBytes(storageRef, updates.photoFile);
      photoURL = await getDownloadURL(storageRef);
    }
    await updateProfile(user, {
      displayName: updates.displayName || user.displayName,
      photoURL: photoURL
    });
    const userDocRef = doc(db, 'users', uid);
    await updateDoc(userDocRef, {
      displayName: updates.displayName || user.displayName,
      photoURL: photoURL,
      updatedAt: serverTimestamp()
    });
    const isAdmin = await checkIsAdmin(user.uid);
    return {
      uid: user.uid,
      email: user.email,
      displayName: updates.displayName || user.displayName,
      photoURL: photoURL,
      isPro: false,
      isAdmin 
    };
  } else {
    return new Promise((resolve) => {
      setTimeout(() => {
        const stored = isBrowser ? localStorage.getItem('ctt_user') : null;
        const currentUser = stored ? JSON.parse(stored) : {};
        const mockPhotoUrl = updates.photoFile 
          ? (isBrowser ? URL.createObjectURL(updates.photoFile) : '') 
          : currentUser.photoURL;
        const updatedUser = {
          ...currentUser,
          displayName: updates.displayName || currentUser.displayName,
          photoURL: mockPhotoUrl
        };
        if (isBrowser) {
          localStorage.setItem('ctt_user', JSON.stringify(updatedUser));
        }
        resolve(updatedUser);
      }, 1000);
    });
  }
};

export const logAppEvent = (eventName: string, eventParams?: { [key: string]: any }) => {
  if (isRealFirebase && analytics) {
    logEvent(analytics, eventName, eventParams);
  } else {
    console.log(`[Fallback Analytics] Event: ${eventName}`, eventParams);
  }
};

// --- TEAM SERVICE ---
export const getTeamMembers = async (): Promise<TeamMember[]> => {
  if (isRealFirebase) {
    const q = query(collection(db, 'team_members'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TeamMember));
  } else {
    return fallbackGetTeamMembers();
  }
};

export const inviteMember = async (email: string, role: UserRole): Promise<TeamMember> => {
  if (isRealFirebase) {
    const inviteRef = await addDoc(collection(db, 'invites'), {
      email,
      role,
      status: 'pending',
      createdAt: serverTimestamp()
    });
    const newMemberData = {
      email,
      role,
      status: 'Invited' as const,
      displayName: email.split('@')[0],
      photoURL: `https://ui-avatars.com/api/?name=${email}&background=random`,
      dateAdded: new Date().toLocaleDateString(),
    };
    await addDoc(collection(db, 'team_members'), newMemberData);
    return { id: inviteRef.id, ...newMemberData } as TeamMember;
  } else {
    return fallbackInviteMember(email, role);
  }
};

export const removeMember = async (id: string): Promise<void> => {
  if (isRealFirebase) {
    await deleteDoc(doc(db, 'team_members', id));
  } else {
    return fallbackRemoveMember(id);
  }
};

// --- BLOG SERVICE ---
export const getBlogPosts = async (): Promise<BlogPost[]> => {
  if (isRealFirebase) {
    try {
      const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        let createdAt: string;
        if (data.createdAt?.toDate) {
          createdAt = data.createdAt.toDate().toISOString();
        } else if (typeof data.createdAt === 'string') {
          createdAt = data.createdAt;
        } else {
          console.warn(`Post "${data.title}" has invalid createdAt`);
          createdAt = new Date().toISOString();
        }
        return {
          id: doc.id,
          ...data,
          createdAt
        } as BlogPost;
      });
    } catch (error) {
      console.warn("Firestore Read Error (falling back to empty list):", error);
      return fallbackGetBlogPosts();
    }
  } else {
    return fallbackGetBlogPosts();
  }
};

export const getBlogPost = async (idOrSlug: string): Promise<BlogPost | null> => {
  if (isRealFirebase) {
    try {
      const docRef = doc(db, 'posts', idOrSlug);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
        } as BlogPost;
      }
      
      const q = query(collection(db, 'posts'), where('slug', '==', idOrSlug));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
        } as BlogPost;
      }
      return null;
    } catch (error) {
      console.warn("Firestore Read Error:", error);
      return null;
    }
  } else {
    const posts = await fallbackGetBlogPosts();
    return posts.find(p => p.id === idOrSlug || p.slug === idOrSlug) || null;
  }
};

export const createBlogPost = async (postData: Partial<BlogPost>, coverImageFile?: File): Promise<void> => {
  if (isRealFirebase) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Must be logged in");
      
      const isAdmin = await checkIsAdmin(user.uid);
      if (!isAdmin) throw new Error("Unauthorized");

      let coverImageUrl = '';
      if (coverImageFile) {
        const storageRef = ref(storage, `blog_covers/${Date.now()}_${coverImageFile.name}`);
        await uploadBytes(storageRef, coverImageFile);
        coverImageUrl = await getDownloadURL(storageRef);
      }
      const slug = createSlug(postData.title || '');
      await addDoc(collection(db, 'posts'), {
        ...postData,
        slug,
        coverImageUrl: coverImageUrl || postData.coverImageUrl || null,
        authorId: user.uid,
        authorName: user.displayName || 'Anonymous',
        authorPhoto: user.photoURL,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Firestore Write Error (falling back):", error);
      return fallbackCreateBlogPost(postData, coverImageFile);
    }
  } else {
    return fallbackCreateBlogPost(postData, coverImageFile);
  }
};

export const updateBlogPost = async (id: string, postData: Partial<BlogPost>, coverImageFile?: File): Promise<void> => {
  if (isRealFirebase) {
    try {
      const updatePayload: any = {
        ...postData,
        updatedAt: serverTimestamp()
      };
      if (coverImageFile) {
        const storageRef = ref(storage, `blog_covers/${Date.now()}_${coverImageFile.name}`);
        await uploadBytes(storageRef, coverImageFile);
        updatePayload.coverImageUrl = await getDownloadURL(storageRef);
      } else if (postData.coverImageUrl !== undefined) {
         updatePayload.coverImageUrl = postData.coverImageUrl;
      }
      if (postData.title) {
        updatePayload.slug = createSlug(postData.title);
      }
      const docRef = doc(db, 'posts', id);
      await updateDoc(docRef, updatePayload);
    } catch (error) {
      console.error("Firestore Update Error (falling back):", error);
      return fallbackUpdateBlogPost(id, postData, coverImageFile);
    }
  } else {
    return fallbackUpdateBlogPost(id, postData, coverImageFile);
  }
};

export const deleteBlogPost = async (id: string): Promise<void> => {
  if (isRealFirebase) {
    try {
      await deleteDoc(doc(db, 'posts', id));
    } catch (error) {
      console.error("Firestore Delete Error (falling back):", error);
      return fallbackDeleteBlogPost(id);
    }
  } else {
    return fallbackDeleteBlogPost(id);
  }
};

// --- FACTORY DROPS SERVICE ---
export const getFactoryDrops = async (): Promise<FactoryDrop[]> => {
  if (isRealFirebase) {
    try {
      const q = query(collection(db, 'factory_drops'), orderBy('date', 'desc'));
      const qs = await getDocs(q);
      return qs.docs.map(d => ({ id: d.id, ...d.data() } as FactoryDrop));
    } catch (e) { return fallbackGetFactoryDrops(); }
  }
  return fallbackGetFactoryDrops();
};

export const createFactoryDrop = async (drop: Partial<FactoryDrop>): Promise<void> => {
  if (isRealFirebase) {
    const user = auth.currentUser;
    if (!user) throw new Error("Unauthorized");
    
    const isAdmin = await checkIsAdmin(user.uid);
    if (!isAdmin) throw new Error("Unauthorized");
    
    await addDoc(collection(db, 'factory_drops'), { ...drop, date: new Date().toISOString() });
  } else {
    return fallbackCreateFactoryDrop(drop);
  }
};

export const deleteFactoryDrop = async (id: string): Promise<void> => {
  if (isRealFirebase) {
    const user = auth.currentUser;
    if (!user) throw new Error("Unauthorized");
    
    const isAdmin = await checkIsAdmin(user.uid);
    if (!isAdmin) throw new Error("Unauthorized");
    
    await deleteDoc(doc(db, 'factory_drops', id));
  } else {
    return fallbackDeleteFactoryDrop(id);
  }
};

// --- PRODUCTION SCHEDULE SERVICE ---
export const getProductionSchedule = async (): Promise<ProductionItem[]> => {
  if (isRealFirebase) {
    try {
      const q = query(collection(db, 'production_schedule'), orderBy('votes', 'desc'));
      const qs = await getDocs(q);
      return qs.docs.map(d => ({ id: d.id, ...d.data() } as ProductionItem));
    } catch (e) { return fallbackGetProductionSchedule(); }
  }
  return fallbackGetProductionSchedule();
};

export const createProductionItem = async (item: Partial<ProductionItem>): Promise<void> => {
  if (isRealFirebase) {
    const user = auth.currentUser;
    if (!user) throw new Error("Unauthorized");
    
    const isAdmin = await checkIsAdmin(user.uid);
    if (!isAdmin) throw new Error("Unauthorized");
    
    await addDoc(collection(db, 'production_schedule'), item);
  } else {
    return fallbackCreateProductionItem(item);
  }
};

export const deleteProductionItem = async (id: string): Promise<void> => {
  if (isRealFirebase) {
    const user = auth.currentUser;
    if (!user) throw new Error("Unauthorized");
    
    const isAdmin = await checkIsAdmin(user.uid);
    if (!isAdmin) throw new Error("Unauthorized");
    
    await deleteDoc(doc(db, 'production_schedule', id));
  } else {
    return fallbackDeleteProductionItem(id);
  }
};

export const voteForProductionItem = async (id: string): Promise<void> => {
  if (isRealFirebase) {
    const itemRef = doc(db, 'production_schedule', id);
    await updateDoc(itemRef, { votes: increment(1) });
  } else {
    return fallbackVoteForItem(id);
  }
};

// --- APP PLAN DECOMMISSION SERVICE ---
export const deleteAccount = async (): Promise<void> => {
  if (isRealFirebase) {
    if (!app) throw new Error("Firebase App not initialized");
    const functions = getFunctions(app, 'us-central1');
    const deleteFn = httpsCallable(functions, 'deleteUserAccount');
    await deleteFn({ appId: 'codetotext' });
  } else {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (isBrowser) {
          const stored = localStorage.getItem('ctt_user');
          if (stored) {
            const user = JSON.parse(stored);
            user.isPro = false; 
            localStorage.setItem('ctt_user', JSON.stringify(user));
          }
          window.location.reload();
        }
        resolve();
      }, 800);
    });
  }
};

// --- FALLBACK IMPLEMENTATIONS ---
let fallbackTeamMembers: TeamMember[] = [
  {
    id: 'user_123456',
    email: 'demo@example.com',
    displayName: 'Demo User',
    photoURL: 'https://picsum.photos/100/100',
    role: 'Admin',
    status: 'Active',
    dateAdded: new Date().toLocaleDateString(),
  },
];
let fallbackBlogPosts: BlogPost[] = [];
let fallbackDrops: FactoryDrop[] = [
  { id: 'drop1', title: 'Cursor.sh Ruleset', desc: 'Internal .cursorrules file.', type: 'Config', date: 'Dec 02', iconType: 'json' },
  { id: 'drop2', title: 'Production TSConfig', desc: 'Strict type-checking setup.', type: 'Template', date: 'Nov 25', iconType: 'code' },
  { id: 'drop3', title: 'Linear Issue Templates', desc: 'Bug report templates.', type: 'Workflow', date: 'Nov 18', iconType: 'box' }
];
let fallbackSchedule: ProductionItem[] = [
  { id: 'p1', name: 'ApiSmith', status: 'In Dev', votes: 142 },
  { id: 'p2', name: 'LogStream', status: 'Concept', votes: 89 },
  { id: 'p3', name: 'EnvVault', status: 'Concept', votes: 56 },
];

const fallbackGetTeamMembers = async (): Promise<TeamMember[]> => new Promise(r => setTimeout(() => r([...fallbackTeamMembers]), 600));
const fallbackInviteMember = async (email: string, role: UserRole): Promise<TeamMember> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const newMember: TeamMember = {
          id: `mem_${Date.now()}`,
          email,
          displayName: email.split('@')[0],
          role,
          status: 'Invited',
          dateAdded: new Date().toLocaleDateString(),
        };
        fallbackTeamMembers.push(newMember);
        resolve(newMember);
      }, 800);
    });
};
const fallbackRemoveMember = async (id: string): Promise<void> => new Promise(r => { fallbackTeamMembers = fallbackTeamMembers.filter(m => m.id !== id); r(); });

const fallbackGetBlogPosts = async (): Promise<BlogPost[]> => new Promise(resolve => setTimeout(() => resolve([...fallbackBlogPosts]), 600));
const fallbackCreateBlogPost = async (data: Partial<BlogPost>, file?: File): Promise<void> => {
  return new Promise(resolve => setTimeout(() => {
    console.warn('⚠️ Fallback mode: Blog post not actually saved to Firestore');
    const newPost: BlogPost = {
      id: `post_${Date.now()}`,
      title: data.title || 'Untitled',
      slug: createSlug(data.title || 'untitled'),
      excerpt: data.excerpt || '',
      content: data.content || '',
      category: data.category,
      coverImageUrl: file && isBrowser ? URL.createObjectURL(file) : undefined,
      authorId: 'user_admin',
      authorName: 'Five Dollar',
      createdAt: new Date().toISOString(),
      readTime: '5 min read',
      isPublic: data.isPublic
    };
    fallbackBlogPosts.unshift(newPost);
    resolve();
  }, 1000));
};
const fallbackUpdateBlogPost = async (id: string, data: Partial<BlogPost>, file?: File): Promise<void> => {
  return new Promise(resolve => setTimeout(() => {
    console.warn('⚠️ Fallback mode: Blog post not actually updated in Firestore');
    const idx = fallbackBlogPosts.findIndex(p => p.id === id);
    if (idx !== -1) {
      const updatedSlug = data.title ? createSlug(data.title) : fallbackBlogPosts[idx].slug;
      fallbackBlogPosts[idx] = { 
        ...fallbackBlogPosts[idx], 
        ...data,
        slug: updatedSlug,
        coverImageUrl: file && isBrowser ? URL.createObjectURL(file) : fallbackBlogPosts[idx].coverImageUrl,
        updatedAt: new Date().toISOString()
      };
    }
    resolve();
  }, 1000));
};
const fallbackDeleteBlogPost = async (id: string): Promise<void> => new Promise(r => { 
  console.warn('⚠️ Fallback mode: Blog post not actually deleted from Firestore');
  fallbackBlogPosts = fallbackBlogPosts.filter(p => p.id !== id); 
  r(); 
});

const fallbackGetFactoryDrops = async () => new Promise<FactoryDrop[]>(r => setTimeout(() => r([...fallbackDrops]), 500));
const fallbackCreateFactoryDrop = async (d: Partial<FactoryDrop>) => new Promise<void>(r => { 
    fallbackDrops.unshift({ id: `drop_${Date.now()}`, date: new Date().toLocaleDateString(), ...d } as FactoryDrop); r(); 
});
const fallbackDeleteFactoryDrop = async (id: string) => new Promise<void>(r => { fallbackDrops = fallbackDrops.filter(d => d.id !== id); r(); });

const fallbackGetProductionSchedule = async () => new Promise<ProductionItem[]>(r => setTimeout(() => r([...fallbackSchedule]), 500));
const fallbackCreateProductionItem = async (i: Partial<ProductionItem>) => new Promise<void>(r => {
    fallbackSchedule.push({ id: `item_${Date.now()}`, votes: 0, ...i } as ProductionItem); r();
});
const fallbackDeleteProductionItem = async (id: string) => new Promise<void>(r => { fallbackSchedule = fallbackSchedule.filter(i => i.id !== id); r(); });
const fallbackVoteForItem = async (id: string) => new Promise<void>(r => {
    const item = fallbackSchedule.find(i => i.id === id);
    if (item) item.votes++;
    r();
});

export const getFirebaseApp = () => isRealFirebase ? app : null;
export { app, auth, db, storage, analytics };